import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// A4 dimensions at 150 DPI — good balance between quality and file size.
// 210mm × 297mm @ 150 DPI = 1240 × 1754 px
const A4_WIDTH_PX = 1240;
const A4_HEIGHT_PX = 1754;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const A4_MARGIN_MM = 10; // 10mm margin on all sides

/**
 * Renders the element to a canvas, then composes it onto an A4-sized
 * white canvas — scaled to fit and centered, with a small margin.
 * This guarantees a true A4 output for both PDF and PNG.
 */
const renderToA4Canvas = async (element) => {
  // Step 1: high-res capture of the certificate
  const captured = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  // Step 2: build an A4-sized white canvas
  const a4 = document.createElement('canvas');
  a4.width = A4_WIDTH_PX;
  a4.height = A4_HEIGHT_PX;
  const ctx = a4.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, A4_WIDTH_PX, A4_HEIGHT_PX);

  // Step 3: compute scale that fits the capture inside A4 with margin
  const marginPx = (A4_MARGIN_MM / A4_WIDTH_MM) * A4_WIDTH_PX; // ~59px
  const maxW = A4_WIDTH_PX - marginPx * 2;
  const maxH = A4_HEIGHT_PX - marginPx * 2;
  const scale = Math.min(maxW / captured.width, maxH / captured.height);
  const drawW = captured.width * scale;
  const drawH = captured.height * scale;
  const drawX = (A4_WIDTH_PX - drawW) / 2;
  const drawY = marginPx; // top-aligned within margin

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(captured, drawX, drawY, drawW, drawH);

  return a4;
};

export const generatePDF = async (elementId, fileName = 'certificate') => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      alert('Certificate preview not found');
      return;
    }

    const a4Canvas = await renderToA4Canvas(element);
    const imgData = a4Canvas.toDataURL('image/jpeg', 0.95);

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    // Single A4 page, image fills the whole page edge-to-edge
    // (margin is already baked into the canvas)
    pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, undefined, 'FAST');
    pdf.save(`${(fileName || 'certificate').replace(/\s+/g, '_')}_certificate_A4.pdf`);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error('Error generating PDF:', error);
    alert('Failed to generate PDF. Please try again.');
  }
};

export const generateImage = async (elementId, fileName = 'certificate') => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      alert('Certificate preview not found');
      return;
    }

    const a4Canvas = await renderToA4Canvas(element);

    a4Canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(fileName || 'certificate').replace(/\s+/g, '_')}_certificate_A4.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      'image/png',
      1.0
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error('Error generating image:', error);
    alert('Failed to generate image. Please try again.');
  }
};
